import projectApi from 'api/projectApi'
import { invalidateReportsOnSuccess } from 'api/reportInvalidation'
import { IApiResponse } from 'api/types'
import {
  IDwgImportRecord,
  ILayoutAutoGenerateRequest,
  ILayoutRecord,
  IProjectAssignmentRecord,
  IProjectRecord,
  ProjectListRequest,
  ProjectListResponse
} from './types'

const projectApiSlice = projectApi.injectEndpoints({
  endpoints: (build) => ({
    getProjects: build.query<ProjectListResponse, ProjectListRequest>({
      query: (body) => ({
        url: '/projects/list',
        method: 'POST',
        data: body
      }),
      transformResponse: (resp: IApiResponse<ProjectListResponse>) =>
        resp.data,
      providesTags: ['ProjectList']
    }),
    getProject: build.query<IProjectRecord, string>({
      query: (id) => ({ url: `/projects/${id}`, method: 'GET' }),
      transformResponse: (resp: IApiResponse<IProjectRecord>) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'Project', id }]
    }),
    createProject: build.mutation<IProjectRecord, Record<string, unknown>>({
      query: (data) => ({ url: '/projects', method: 'POST', data }),
      transformResponse: (resp: IApiResponse<IProjectRecord>) => resp.data,
      invalidatesTags: ['ProjectList'],
      // Project-level charges (registration, cancellation, taxes) and the
      // project list itself feed the reports, which are cached on a separate
      // createApi instance these tags can't reach.
      onQueryStarted: invalidateReportsOnSuccess
    }),
    updateProject: build.mutation<
      IProjectRecord,
      { id: string, data: Record<string, unknown> }
    >({
      query: ({ id, data }) => ({
        url: `/projects/${id}`,
        method: 'PATCH',
        data
      }),
      transformResponse: (resp: IApiResponse<IProjectRecord>) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Project', id },
        'ProjectList'
      ],
      onQueryStarted: invalidateReportsOnSuccess
    }),
    deleteProject: build.mutation<void, string>({
      query: (id) => ({ url: `/projects/${id}`, method: 'DELETE' }),
      invalidatesTags: ['ProjectList'],
      onQueryStarted: invalidateReportsOnSuccess
    }),

    // ---------- assignments ----------

    getMyProjectAssignments: build.query<IProjectAssignmentRecord[], void>({
      query: () => ({ url: '/me/project-assignments', method: 'GET' }),
      transformResponse: (resp: IApiResponse<IProjectAssignmentRecord[]>) =>
        resp.data
    }),

    // ---------- layouts ----------

    getProjectLayout: build.query<ILayoutRecord, string>({
      query: (projectId) => ({
        url: `/projects/${projectId}/layout`,
        method: 'GET'
      }),
      transformResponse: (resp: IApiResponse<ILayoutRecord>) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'ProjectLayout', id }]
    }),
    createBlankLayout: build.mutation<
      ILayoutRecord,
      { projectId: string, canvasWidth?: number, canvasHeight?: number }
    >({
      query: ({ projectId, canvasWidth = 1200, canvasHeight = 800 }) => ({
        url: `/projects/${projectId}/layout`,
        method: 'POST',
        params: { canvasWidth, canvasHeight }
      }),
      transformResponse: (resp: IApiResponse<ILayoutRecord>) => resp.data,
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'ProjectLayout', id: projectId }
      ]
    }),
    autoGenerateLayout: build.mutation<
      ILayoutRecord,
      { projectId: string, body: ILayoutAutoGenerateRequest }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/layout/auto-generate`,
        method: 'POST',
        data: body
      }),
      transformResponse: (resp: IApiResponse<ILayoutRecord>) => resp.data,
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'ProjectLayout', id: projectId },
        { type: 'Project', id: projectId },
        'ProjectList'
      ]
    }),
    publishLayout: build.mutation<ILayoutRecord, string>({
      query: (projectId) => ({
        url: `/projects/${projectId}/layout/publish`,
        method: 'POST'
      }),
      transformResponse: (resp: IApiResponse<ILayoutRecord>) => resp.data,
      invalidatesTags: (_r, _e, projectId) => [
        { type: 'ProjectLayout', id: projectId },
        { type: 'Project', id: projectId },
        'ProjectList'
      ]
    }),
    unpublishLayout: build.mutation<ILayoutRecord, string>({
      query: (projectId) => ({
        url: `/projects/${projectId}/layout/unpublish`,
        method: 'POST'
      }),
      transformResponse: (resp: IApiResponse<ILayoutRecord>) => resp.data,
      invalidatesTags: (_r, _e, projectId) => [
        { type: 'ProjectLayout', id: projectId },
        { type: 'Project', id: projectId },
        'ProjectList'
      ]
    }),

    // ---------- DWG imports ----------

    uploadDwgImport: build.mutation<
      IDwgImportRecord,
      { projectId: string, file: File }
    >({
      query: ({ projectId, file }) => {
        const fd = new FormData()
        fd.append('file', file)
        return {
          url: `/projects/${projectId}/layout/import-dwg`,
          method: 'POST',
          data: fd
        }
      },
      transformResponse: (resp: IApiResponse<IDwgImportRecord>) => resp.data,
      // Invalidate the imports list, the consolidated dwg-data, and the
      // saved project layout so every reader (imports list, DWG canvas,
      // layout canvas) refetches automatically after a fresh upload.
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'DwgImports', id: projectId },
        { type: 'DwgData', id: projectId },
        { type: 'ProjectLayout', id: projectId }
      ]
    }),
    // Replace an already-imported DWG with a new file. Backend swaps the
    // source drawing in-place and re-extracts the layout, so we hit the
    // same cache tags as `uploadDwgImport` to refresh every downstream
    // reader.
    replaceDwgImport: build.mutation<
      IDwgImportRecord,
      { projectId: string, file: File }
    >({
      query: ({ projectId, file }) => {
        const fd = new FormData()
        fd.append('file', file)
        return {
          url: `/projects/${projectId}/layout/import-dwg`,
          method: 'PUT',
          data: fd
        }
      },
      transformResponse: (resp: IApiResponse<IDwgImportRecord>) => resp.data,
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'DwgImports', id: projectId },
        { type: 'DwgData', id: projectId },
        { type: 'ProjectLayout', id: projectId }
      ]
    }),
    getDwgImports: build.query<IDwgImportRecord[], string>({
      query: (projectId) => ({
        url: `/projects/${projectId}/layout/dwg-imports`,
        method: 'GET'
      }),
      transformResponse: (resp: IApiResponse<IDwgImportRecord[]>) =>
        Array.isArray(resp?.data) ? resp.data : [],
      providesTags: (_r, _e, projectId) => [
        { type: 'DwgImports', id: projectId }
      ]
    }),
    getDwgImport: build.query<
      IDwgImportRecord,
      { projectId: string, importId: string }
    >({
      query: ({ projectId, importId }) => ({
        url: `/projects/${projectId}/layout/dwg-imports/${importId}`,
        method: 'GET'
      }),
      transformResponse: (resp: IApiResponse<IDwgImportRecord>) => resp.data
    }),
    // Consolidated DWG payload for a project — returned shape is
    // backend-defined (entities / layers / blocks blob). Auto-refetches
    // whenever a new DWG is uploaded for the same project via the
    // `DwgData` tag wired on `uploadDwgImport`.
    getProjectDwgData: build.query<unknown, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/layout/dwg-data`,
        method: 'GET'
      }),
      transformResponse: (resp: IApiResponse<unknown>) => resp.data,
      providesTags: (_r, _e, { projectId }) => [
        { type: 'DwgData', id: projectId }
      ]
    })
  })
})

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetMyProjectAssignmentsQuery,
  useGetProjectLayoutQuery,
  useCreateBlankLayoutMutation,
  useAutoGenerateLayoutMutation,
  usePublishLayoutMutation,
  useUnpublishLayoutMutation,
  useUploadDwgImportMutation,
  useReplaceDwgImportMutation,
  useGetDwgImportsQuery,
  useGetDwgImportQuery,
  useLazyGetDwgImportQuery,
  useGetProjectDwgDataQuery,
  useLazyGetProjectDwgDataQuery
} = projectApiSlice

export default projectApiSlice
